import { Component, ElementRef, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { EbayShippingCarrierResponseModel } from '../../../../shared/models/response-models/ebay-shipping-carrier.model';
import { EbayShippingCarrierService } from '../../../../shared/services/ebay-shipping-carrier.service';
import { ErrorHandlerService } from '../../../../shared/services/error-handler.service';
import { MasterService } from '../../../../shared/services/master.service';
import { ToasterService } from '../../../../shared/services/toaster.service';
import { MARKETPLACE_OPTIONS, Marketplace, MarketplaceOption } from '../../enums/marketplace.enum';
import { CarrierModel } from '../../models/carriers.model';
import { CarrierMarketplaceMappingViewModel } from '../../models/view-models/carrier-marketplace-mapping-view.model';
import { CarrierMarketplaceMappingMapperService } from '../../services/carrier-marketplace-mapping-mapper.service';
import { CarrierMarketplaceMappingsApiService } from '../../services/carrier-marketplace-mappings-api.service';
import { CarriersApiService } from '../../services/carriers-api.service';

@Component({
  selector: 'app-add-edit-carrier-marketplace-mapping',
  standalone: false,
  templateUrl: './add-edit-carrier-marketplace-mapping.component.html',
  styleUrl: './add-edit-carrier-marketplace-mapping.component.css',
})
export class AddEditCarrierMarketplaceMappingComponent implements OnInit {
  @Output() triggerParentMethod = new EventEmitter();
  @ViewChild('closeBtn') closeBtn?: ElementRef<HTMLElement>;

  frmText: string = 'Add Carrier Marketplace Mapping';
  carrierMapping: CarrierMarketplaceMappingViewModel = new CarrierMarketplaceMappingViewModel();
  carriers: CarrierModel[] = [];
  ebayShippingCarriers: EbayShippingCarrierResponseModel[] = [];
  filteredShippingCarriers: EbayShippingCarrierResponseModel[] = [];
  marketplaceOptions: MarketplaceOption[] = MARKETPLACE_OPTIONS;
  Marketplace = Marketplace;
  companyId: number = 0;
  isLoading: boolean = false;
  isShippingCarrierLoading: boolean = false;

  constructor(
    private readonly carriersApiService: CarriersApiService,
    private readonly mappingsApiService: CarrierMarketplaceMappingsApiService,
    private readonly mappingMapperService: CarrierMarketplaceMappingMapperService,
    private readonly ebayShippingCarrierService: EbayShippingCarrierService,
    private readonly toastService: ToasterService,
    private readonly masterService: MasterService,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {}

  ngOnInit(): void {
    const userInfo = this.masterService.getUserDetailFromStorage();
    this.companyId = Number(userInfo.companyId);
    this.loadCarriers();
  }

  async initializeRecord(id: number): Promise<void> {
    this.carrierMapping = new CarrierMarketplaceMappingViewModel();
    this.ebayShippingCarriers = [];
    this.filteredShippingCarriers = [];
    this.isShippingCarrierLoading = false;

    if (id > 0) {
      this.frmText = 'Edit Carrier Marketplace Mapping';
      await this.getMappingById(id);
      await this.loadShippingCarriersByMarketplace(this.carrierMapping.marketplace);
      return;
    }

    this.frmText = 'Add Carrier Marketplace Mapping';
  }

  loadCarriers(): void {
    const queryParams: Record<string, string | number | boolean> = {
      CompanyId: this.companyId,
    };

    this.carriersApiService.getCarriers(queryParams).subscribe({
      next: (response) => {
        this.carriers = response ?? [];
      },
      error: (error) => {
        this.errorHandlerService.handleApiError(error);
      },
    });
  }

  async getMappingById(id: number): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.mappingsApiService.getCarrierMarketplaceMappingById(id),
      );
      this.carrierMapping = this.mappingMapperService.buildViewFromResponse(response);
    } catch (error) {
      this.errorHandlerService.handleApiError(error);
    }
  }

  async onMarketplaceChange(marketplace: Marketplace): Promise<void> {
    this.carrierMapping.marketplace = marketplace;
    this.carrierMapping.shippingName = '';
    this.carrierMapping.shippingCode = '';
    await this.loadShippingCarriersByMarketplace(marketplace);
  }

  onShippingNameChange(selectedShippingName: string): void {
    const selectedCarrier = this.filteredShippingCarriers.find(
      (item) => item.description === selectedShippingName,
    );

    this.carrierMapping.shippingCode = selectedCarrier?.shippingCarrier ?? '';
  }

  searchShippingCarrier(term: string, item: EbayShippingCarrierResponseModel): boolean {
    const normalizedTerm = (term ?? '').trim().toLowerCase();

    if (!normalizedTerm) {
      return true;
    }

    return (item.description ?? '').toLowerCase().includes(normalizedTerm);
  }

  private toShippingCarrierModel(item: unknown): EbayShippingCarrierResponseModel | null {
    if (!item || typeof item !== 'object') {
      return null;
    }

    const obj = item as Record<string, unknown>;
    const description = obj['description'] ?? obj['Description'] ?? obj['name'] ?? obj['Name'];
    const shippingCarrier =
      obj['shippingCarrier'] ?? obj['ShippingCarrier'] ?? obj['code'] ?? obj['Code'];

    if (!description && !shippingCarrier) {
      return null;
    }

    return {
      description: String(description ?? ''),
      shippingCarrier: String(shippingCarrier ?? ''),
    };
  }

  private normalizeShippingCarrierResponse(
    response: unknown,
  ): EbayShippingCarrierResponseModel[] {
    let rawItems: unknown[] = [];

    if (Array.isArray(response)) {
      rawItems = response;
    } else if (response && typeof response === 'object') {
      const obj = response as Record<string, unknown>;

      const knownArrayKeys = [
        'items',
        'Items',
        'data',
        'Data',
        'value',
        'Value',
        'result',
        'Result',
        'results',
        'Results',
        'shippingCarriers',
        'ShippingCarriers',
        'carriers',
        'Carriers',
      ];

      for (const key of knownArrayKeys) {
        if (Array.isArray(obj[key])) {
          rawItems = obj[key] as unknown[];
          break;
        }
      }

      if (rawItems.length === 0) {
        const firstArrayValue = Object.values(obj).find((value) => Array.isArray(value));
        if (Array.isArray(firstArrayValue)) {
          rawItems = firstArrayValue as unknown[];
        }
      }

      if (rawItems.length === 0) {
        const singleItem = this.toShippingCarrierModel(obj);
        return singleItem ? [singleItem] : [];
      }
    }

    return rawItems
      .map((item) => this.toShippingCarrierModel(item))
      .filter((item): item is EbayShippingCarrierResponseModel => item !== null);
  }

  async loadShippingCarriersByMarketplace(marketplace: Marketplace): Promise<void> {
    if (marketplace !== Marketplace.eBay) {
      this.ebayShippingCarriers = [];
      this.filteredShippingCarriers = [];
      this.isShippingCarrierLoading = false;
      return;
    }

    try {
      this.isShippingCarrierLoading = true;
      const response: unknown = await firstValueFrom(
        this.ebayShippingCarrierService.getShippingCarriers(),
      );
      this.ebayShippingCarriers = this.normalizeShippingCarrierResponse(response);
      this.filteredShippingCarriers = [...this.ebayShippingCarriers];

      if (this.carrierMapping.shippingName) {
        this.onShippingNameChange(this.carrierMapping.shippingName);
      }
    } catch (error) {
      this.errorHandlerService.handleApiError(error);
      this.ebayShippingCarriers = [];
      this.filteredShippingCarriers = [];
    } finally {
      this.isShippingCarrierLoading = false;
    }
  }

  showRequiredFieldsMessage(event: MouseEvent): void {
    if (this.isSaveDisabled()) {
      event.preventDefault();
      this.toastService.error('Please fill all required fields before saving.');
    }
  }

  isSaveDisabled(): boolean {
    return (
      !this.carrierMapping.carrierId ||
      this.carrierMapping.marketplace === Marketplace.Unknown ||
      !this.carrierMapping.shippingName ||
      !this.carrierMapping.shippingCode
    );
  }

  manageCarrierMarketplaceMappingOperation(): void {
    this.carrierMapping.companyId = this.companyId;

    if (this.carrierMapping.id > 0) {
      this.updateRecord();
      return;
    }

    this.addNewRecord();
  }

  addNewRecord(): void {
    this.isLoading = true;
    const payload = this.mappingMapperService.buildRequestFromView(this.carrierMapping);

    this.mappingsApiService.createCarrierMarketplaceMapping(payload).subscribe({
      next: () => {
        this.toastService.success('Carrier marketplace mapping has been added successfully!');
        this.triggerParentMethod.emit();
        this.closeBtn?.nativeElement.click();
        this.isLoading = false;
      },
      error: (err) => {
        this.errorHandlerService.handleApiError(err);
        this.isLoading = false;
      },
    });
  }

  updateRecord(): void {
    this.isLoading = true;
    const payload = this.mappingMapperService.buildRequestFromView(this.carrierMapping);

    this.mappingsApiService
      .updateCarrierMarketplaceMapping(this.carrierMapping.id, payload)
      .subscribe({
        next: () => {
          this.toastService.success('Carrier marketplace mapping has been updated successfully!');
          this.triggerParentMethod.emit();
          this.closeBtn?.nativeElement.click();
          this.isLoading = false;
        },
        error: (err) => {
          this.errorHandlerService.handleApiError(err);
          this.isLoading = false;
        },
      });
  }
}
